import { useRef, useCallback, useEffect, ReactNode } from 'react';

import styles from './CardGrid.module.scss';

interface CardGridProps<T> {
  items: T[];
  isLastPage: boolean;
  showMore: () => void;
  children: (item: T) => ReactNode;
}

export const CardGrid = <T extends { objectID?: string; id?: string }>({
  items,
  isLastPage,
  showMore,
  children,
}: CardGridProps<T>) => {
  const lastItemRef = useRef<HTMLDivElement>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && !isLastPage) {
        showMore();
      }
    },
    [showMore, isLastPage],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
    });

    if (lastItemRef.current) {
      observer.observe(lastItemRef.current);
    }

    return () => observer.disconnect();
  }, [handleObserver, items.length]);

  return (
    <div className={styles.GridContainer}>
      <div className={styles.Grid}>
        {items.map((hit, index) => (
          <div
            key={hit.objectID || hit.id || index}
            ref={index === items.length - 1 ? lastItemRef : null}
          >
            {children(hit)}
          </div>
        ))}
      </div>
    </div>
  );
};
